import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MapPin, ArrowRight, Thermometer, Weight, Truck, Calendar, Phone,
  MessageCircle, PackageSearch, Search, ShieldCheck, Share2, X, Sparkles, CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TRUCK_TYPES, tempTone, waLink, waShare, loadShareMessage, loadsShareAllMessage, fmtDate } from "@/lib/constants";
import { RatingStars } from "@/components/RatingStars";
import RateDialog from "@/components/RateDialog";
import PostLoadDialog from "@/components/PostLoadDialog";
import GuidedPostDialog from "@/components/GuidedPostDialog";
import AccessGate from "@/components/AccessGate";

function LoadCard({ load, onMessage, onRated, mine }) {
  const wa = waLink(load.poster_phone, `Hi, interested in your load ${load.origin} → ${load.destination} (${load.material_type}).`);
  const markClosed = async () => {
    try { await api.put(`/loads/${load.id}/place`); toast.success("Load marked as closed"); onRated?.(); }
    catch (e) { console.error("Failed to close load:", e); toast.error("Could not update"); }
  };
  return (
    <div data-testid="load-card" className="rounded-xl border border-border bg-card/60 p-4 sm:p-5 hover:border-primary/40 transition-all duration-300 animate-fadeup">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center flex-wrap gap-x-2 gap-y-0.5 font-heading font-semibold text-base sm:text-lg min-w-0">
          <MapPin className="w-4 h-4 text-primary shrink-0" />
          <span className="break-words">{load.origin}</span>
          <ArrowRight className="w-4 h-4 text-muted-foreground shrink-0" />
          <span className="break-words">{load.destination}</span>
        </div>
        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-mono-num border shrink-0 ${tempTone(load.temp_min)}`}>
          <Thermometer className="w-3 h-3" />{load.temp_min}° / {load.temp_max}°
        </span>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-muted-foreground mb-4">
        <div className="flex items-center gap-1.5"><PackageSearch className="w-3.5 h-3.5 text-primary/70" />{load.material_type}</div>
        <div className="flex items-center gap-1.5"><Weight className="w-3.5 h-3.5 text-primary/70" /><span className="font-mono-num">{load.weight}</span> T</div>
        <div className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-primary/70" />{load.truck_type}</div>
        <div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-primary/70" />{fmtDate(load.pickup_date)}</div>
      </div>
      {load.notes && <p className="text-sm text-muted-foreground/80 mb-4 line-clamp-2">{load.notes}</p>}
      <div className="flex flex-wrap items-center justify-between gap-2 pt-3 border-t border-border">
        <div className="text-xs text-muted-foreground truncate flex items-center gap-1.5 min-w-0">
          <span className="text-foreground font-medium truncate">{load.poster_name}</span>
          {load.poster_verified && <ShieldCheck className="w-3.5 h-3.5 text-primary shrink-0" title="Verified transporter" />}
          {load.poster_company ? <span className="truncate">· {load.poster_company}</span> : null}
          <span className="shrink-0"><RatingStars value={load.poster_rating} count={load.poster_rating_count} size={11} /></span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a data-testid="load-share-btn" href={waShare(loadShareMessage(load))} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:border-[#25D366]/60 hover:text-[#25D366] transition-all">
            <Share2 className="w-3.5 h-3.5" /> Share
          </a>
          {mine && (
            <button data-testid="board-close-load-btn" onClick={markClosed} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:border-emerald-400/60 hover:text-emerald-300 transition-all">
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark as closed
            </button>
          )}
          {!mine && <RateDialog userId={load.user_id} name={load.poster_name} onRated={onRated} />}
          {!mine && load.poster_phone && (
            <a data-testid="load-whatsapp-btn" href={wa} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-[#25D366] text-white hover:brightness-110 transition-all">
              <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
            </a>
          )}
          {!mine && (
            <button data-testid="load-message-btn" onClick={() => onMessage(load.user_id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border border-border hover:border-primary/50 hover:text-primary transition-all">
              <Phone className="w-3.5 h-3.5" /> Message
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LoadFeed() {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ origin: "", destination: "", truck_type: "all" });
  const [showWelcome, setShowWelcome] = useState(() => { try { return !localStorage.getItem("rs_welcome_dismissed"); } catch (e) { return true; } });
  const dismissWelcome = () => { try { localStorage.setItem("rs_welcome_dismissed", "1"); } catch (e) {} setShowWelcome(false); };
  const firstName = auth?.user?.name?.split(" ")[0] || "";
  const [guidedOpen, setGuidedOpen] = useState(false);

  const fetchLoads = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/loads", { params: { origin: filters.origin || undefined, destination: filters.destination || undefined, truck_type: filters.truck_type } });
      setLoads(res.data);
    } catch (e) {
      if (e?.response?.status !== 402) toast.error("Could not load board");
    } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { if (auth?.has_access) fetchLoads(); else setLoading(false); }, [fetchLoads, auth]);

  const onMessage = (uid) => navigate(`/app/messages/${uid}`);

  return (
    <AccessGate>
      <div className="max-w-6xl">
        {showWelcome && (
          <div data-testid="welcome-banner" className="mb-6 rounded-xl border border-primary/30 bg-primary/5 p-5 relative animate-fadeup">
            <button data-testid="welcome-dismiss" onClick={dismissWelcome} className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"><X className="w-4 h-4" /></button>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/15 border border-primary/30 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-primary" /></div>
              <div className="min-w-0">
                <h3 className="font-heading font-bold text-lg">Welcome to Reefer Sathi{firstName ? `, ${firstName}` : ""}!</h3>
                <p className="text-sm text-muted-foreground mt-1 mb-3">Post your first load in seconds — add the route, cargo, temperature and date, and it goes live to the whole network instantly. Got a truck free instead? Post it on the Truck Board.</p>
                <div className="flex flex-wrap gap-2">
                  <Button data-testid="welcome-guided-btn" onClick={() => setGuidedOpen(true)} className="rounded-full font-semibold bg-primary text-primary-foreground hover:bg-primary/90"><Sparkles className="w-4 h-4 mr-1.5" /> Post my first load (guided)</Button>
                  <Button data-testid="welcome-post-truck" variant="secondary" onClick={() => navigate("/app/trucks")} className="rounded-full font-semibold"><Truck className="w-4 h-4 mr-1.5" /> Post a Truck</Button>
                </div>
              </div>
            </div>
          </div>
        )}
        <GuidedPostDialog open={guidedOpen} onOpenChange={setGuidedOpen} onCreated={fetchLoads} />
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-extrabold text-3xl tracking-tight">Load Board</h1>
            <p className="text-muted-foreground text-sm mt-1">Live cold-chain freight looking for trucks.</p>
          </div>
          <div className="flex items-center gap-2">
            {loads.filter((l) => l.user_id === auth?.user?.user_id).length > 0 && (
              <a data-testid="share-all-loads-btn" href={waShare(loadsShareAllMessage(loads.filter((l) => l.user_id === auth?.user?.user_id)))} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold border border-border hover:border-[#25D366]/60 hover:text-[#25D366] transition-all">
                <Share2 className="w-4 h-4" /> Share my loads
              </a>
            )}
            <PostLoadDialog onCreated={fetchLoads} />
          </div>
        </div>

        <div className="grid sm:grid-cols-[1fr_1fr_200px_auto] gap-3 mb-6 p-4 rounded-xl border border-border bg-card/40">
          <div className="relative">
            <MapPin className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="filter-origin" className="pl-9" placeholder="Origin" value={filters.origin} onChange={(e) => setFilters(f => ({ ...f, origin: e.target.value }))} />
          </div>
          <div className="relative">
            <MapPin className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="filter-destination" className="pl-9" placeholder="Destination" value={filters.destination} onChange={(e) => setFilters(f => ({ ...f, destination: e.target.value }))} />
          </div>
          <Select value={filters.truck_type} onValueChange={(v) => setFilters(f => ({ ...f, truck_type: v }))}>
            <SelectTrigger data-testid="filter-trucktype"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All truck types</SelectItem>
              {TRUCK_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button data-testid="apply-filters-btn" onClick={fetchLoads} variant="secondary" className="rounded-lg"><Search className="w-4 h-4 mr-1.5" />Search</Button>
        </div>

        {loading ? (
          <p className="text-muted-foreground text-sm py-16 text-center">Loading loads…</p>
        ) : loads.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl">
            <PackageSearch className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-muted-foreground">No loads posted yet. Be the first to post one.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {loads.map((l) => <LoadCard key={l.id} load={l} mine={l.user_id === auth?.user?.user_id} onMessage={onMessage} onRated={fetchLoads} />)}
          </div>
        )}
      </div>
    </AccessGate>
  );
}
